import React, { useContext, memo } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import classNames from 'classnames';
import isEqual from 'lodash.isequal';

import { AppState } from 'services/types';
import { updateQueryString as updateQueryStringAction } from 'services/router/actions';
import ActiveTableCellRowCheckbox from 'components/ActiveTableCellRowCheckbox';
import {
    setHoveredTableCell,
    resetHoveredTableCell
    // @ts-ignore
} from 'services/selectedTableCells/actions';
import {
    makeSelectIsRowHovered,
    makeSelectIsRowActive
    // @ts-ignore
} from 'services/selectedTableCells/selectors';
import { selectVirtualizedTableColumnKeys } from 'services/virtualizedTable/selectors';

import { VirtualizedTableContextProviderValue } from '../../types';
import VirtualizedTableContext from '../VirtualizedTableContext';
import VirtualizedTableCell from '../VirtualizedTableCell';
import VirtualizedTableCellExpandButton from '../VirtualizedTableCellExpandButton';
import {
    SELECT_COLUMN_WIDTH,
    EXPAND_COLUMN_WIDTH,
    FILLER_COLUMN_WIDTH,
    EDIT_MODAL_QUERY_STRING_PARAM
} from '../../constants';

export interface OwnProps {
    rowId?: string;
    style: React.CSSProperties;
}

export interface ConnectedProps {
    columnKeys?: ReturnType<typeof selectVirtualizedTableColumnKeys>;
    isRowHovered?: ReturnType<ReturnType<typeof makeSelectIsRowHovered>>;
    isRowActive?: ReturnType<ReturnType<typeof makeSelectIsRowActive>>;
}

export interface DispatchProps {
    updateQueryString: typeof updateQueryStringAction;
    setHoveredTableCell: typeof setHoveredTableCell;
    resetHoveredTableCell: typeof resetHoveredTableCell;
}

export type Props = OwnProps & ConnectedProps & DispatchProps;

export const VirtualizedTableRowComponent = (
    props: Props
): React.ReactElement | null => {
    const {
        rowId,
        style,
        columnKeys,
        isRowHovered,
        isRowActive,
        updateQueryString
    } = props;

    const {
        onExpand,
        selectable,
        enableItemEditing
    }: VirtualizedTableContextProviderValue = useContext(
        VirtualizedTableContext
    );

    const isExpandable = onExpand || enableItemEditing;

    const handleExpand = (): void => {
        if (onExpand && rowId) {
            onExpand(rowId);
        }
        if (enableItemEditing) {
            updateQueryString({
                [EDIT_MODAL_QUERY_STRING_PARAM]: rowId
            });
        }
    };

    const handleMouseEnter = (): void => {
        props.setHoveredTableCell(rowId);
    };

    const handleMouseLeave = (): void => {
        props.resetHoveredTableCell();
    };

    if (!rowId) {
        return null;
    }

    return (
        <div
            className={classNames('virtualized-table-row', {
                hovered: isRowHovered,
                active: isRowActive
            })}
            style={{
                ...style,
                width: 'auto'
            }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            {selectable && (
                <div
                    className="virtualized-table-cell select"
                    style={{
                        width: SELECT_COLUMN_WIDTH
                    }}
                >
                    <ActiveTableCellRowCheckbox rowId={rowId} />
                </div>
            )}
            {isExpandable && (
                <div
                    className="virtualized-table-cell expand"
                    style={{
                        width: EXPAND_COLUMN_WIDTH
                    }}
                >
                    <VirtualizedTableCellExpandButton
                        rowId={rowId}
                        isRowHovered={isRowHovered}
                        onClick={handleExpand}
                    />
                </div>
            )}
            {columnKeys &&
                columnKeys.map((columnKey) => (
                    <VirtualizedTableCell
                        key={`${rowId}-${columnKey}`}
                        rowId={rowId}
                        cellName={columnKey}
                        columnKey={columnKey}
                        itemKey={rowId}
                    />
                ))}
            <div
                className="virtualized-table-cell"
                style={{
                    width: FILLER_COLUMN_WIDTH
                }}
            />
        </div>
    );
};

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    columnKeys: selectVirtualizedTableColumnKeys,
    isRowHovered: makeSelectIsRowHovered(),
    isRowActive: makeSelectIsRowActive()
});

export default connect(mapStateToProps, {
    updateQueryString: updateQueryStringAction,
    setHoveredTableCell,
    resetHoveredTableCell
})(memo(VirtualizedTableRowComponent, isEqual));
